import { defineComponent, h, nextTick } from 'vue';
import BaseComponent from './BaseComponentMixin.jsx';
import utils from '../../Shared/utils.jsx';
import EventBus from '../event-bus.js';
import {
    QBanner,
    QBtn,
    QCard,
    QCardSection,
    QCheckbox,
    QChip,
    QDialog,
    QExpansionItem,
    QIcon,
    QInput,
    QInnerLoading,
    QMarkupTable,
    QSelect,
    QSeparator,
    QSpace,
    QTab,
    QTabPanel,
    QTabPanels,
    QTabs,
    QToolbar,
    QToolbarTitle,
    QTooltip,
} from 'quasar';
import AceEditor from './3rdParty/AceEditor.jsx';
import ApiTesterParamDescriptionDialog from './ApiTesterParamDescriptionDialog.jsx';

const STORAGE_PREFIX = 'vuePortal.apiTester.';

function stripHtml(html) {
    if (!html || typeof html !== 'string') return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function substituteTemplate(template, values) {
    if (!template) return '';
    return template.replace(/\{([^}]+)\}/g, (_, inner) => {
        const key = inner.replace(/^\*/, '');
        const v = values[key] ?? values[inner] ?? '';
        return encodeURIComponent(v);
    });
}

/** Normalized placeholder names (without leading `*`) present in the route template. */
function templatePlaceholderNames(template) {
    const set = new Set();
    if (!template || typeof template !== 'string') return set;
    template.replace(/\{([^}]+)\}/g, (_, inner) => {
        const key = String(inner).replace(/^\*/, '').trim();
        if (key) set.add(key);
    });
    return set;
}

function argDefaultString(arg, helpByName) {
    const h = helpByName?.[arg.Name];
    const hDef = h?.DefaultValue;
    const aDef = arg.DefaultValue;
    if (hDef != null && String(hDef).trim() !== '') return String(hDef);
    if (aDef != null && String(aDef).trim() !== '') return String(aDef);
    return '';
}

function testValueEqualsDefault(testVal, defaultVal) {
    return String(testVal ?? '').trim() === String(defaultVal ?? '').trim();
}

function verbColor(verb) {
    const v = (verb || 'GET').toUpperCase();
    if (v === 'GET') return 'primary';
    if (v === 'POST') return 'positive';
    if (v === 'PUT' || v === 'PATCH') return 'warning';
    if (v === 'DELETE') return 'negative';
    return 'grey';
}

/** Security context param; omitted from UI and URL — handled by the web server. */
function isAutoContextArg(arg) {
    if (!arg?.Name) return false;
    if (arg.Name !== 'Context') return false;
    const dv = String(arg.DefaultValue ?? '').trim();
    return dv === '{$Current}';
}

function getSecurityContextType(metadata) {
    if (!metadata?.Arguments || !Array.isArray(metadata.Arguments)) return 'Public';
    const s = metadata.Arguments.find((a) => a.SecurityContextType);
    return s ? s.SecurityContextType : 'Public';
}

const DYNAMIC_IMPL_TYPE = 'MethodLib.DynamicImplementation, Shared';
const USER_IMPL_TYPE = 'MethodLib.UserImplementation, Shared';

/** Characters before ellipsis when description is collapsed. */
const PARAM_DESC_PREVIEW = 140;

export default defineComponent({
    name: 'api-tester',
    mixins: [BaseComponent],
    components: {
        AceEditor,
        ApiTesterParamDescriptionDialog,
        QBanner,
        QBtn,
        QCard,
        QCardSection,
        QCheckbox,
        QChip,
        QDialog,
        QExpansionItem,
        QIcon,
        QInput,
        QInnerLoading,
        QMarkupTable,
        QSelect,
        QSeparator,
        QSpace,
        QTab,
        QTabPanel,
        QTabPanels,
        QTabs,
        QToolbar,
        QToolbarTitle,
        QTooltip,
    },
    data() {
        return {
            interpCache: {},
            metadata: null,
            helpDoc: null,
            loading: false,
            loadError: null,
            helpLoadError: null,
            mainTab: 'try',
            sampleLangIndex: 0,
            pathParams: {},
            postBody: '',
            lastResult: null,
            lastResultDisplay: '',
            lastError: null,
            executing: false,
            persistTimer: null,
            fetchGeneration: 0,
            urlOverrideEnabled: false,
            urlOverride: '',
            /** param name -> expanded full description */
            paramDescExpanded: {},
            /** When true, encode query-string parameter values (names always encoded). */
            urlEncodeQueryParams: true,
            responseDialogOpen: false,
            responseAceEditor: null,
            /** JSON.stringify snapshot after load for Cancel / dirty (metadata). */
            _metadataBaselineJson: '',
            _helpDocBaselineJson: '',
            editingSummaryDescription: false,
            /** Visible argument Name when inline-editing metadata argument Description. */
            editingParamDescName: null,
            paramRichDialogOpen: false,
            paramRichDialogParamName: '',
            paramRichDialogInitialHtml: '',
            savingDescriptionEdits: false,
            descriptionSaveError: null,
        };
    },
    computed: {
        rootStyle() {
            return {
                position: 'relative',
                minHeight: '120px',
                minWidth: 0,
                width: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                ...utils.resolveStyleHints(this.styleHints, this),
                ...this.sizeStyle,
            };
        },
        resolvedKey() {
            return String(this.evalControlField('Key') ?? '');
        },
        resolvedName() {
            return this.evalControlField('Name');
        },
        resolvedNamespace() {
            return this.evalControlField('Namespace');
        },
        resolvedDescription() {
            return this.evalControlField('Description');
        },
        resolvedVerb() {
            return this.evalControlField('Verb');
        },
        resolvedUri() {
            return this.evalControlField('Uri');
        },
        resolvedOwnerID() {
            return String(this.evalControlField('OwnerID') ?? '');
        },
        resolvedPublic() {
            const p = this.evalControlField('Public');
            return p === true || p === 'true';
        },
        showLiveWarning() {
            return !this.System.DEBUG;
        },
        configuration() {
            return this.metadata?.Configuration || null;
        },
        httpVerb() {
            const v = this.configuration?.HttpVerb || this.resolvedVerb || 'GET';
            return String(v).toUpperCase();
        },
        displayName() {
            return this.metadata?.Name || this.resolvedName || 'API';
        },
        templatePath() {
            return this.configuration?.Template || this.resolvedUri || '';
        },
        /** Names of arguments that appear as `{name}` or `{*name}` in the template. */
        templateArgNames() {
            return templatePlaceholderNames(this.templatePath);
        },
        builtUrl() {
            const template = this.templatePath || '';
            const pathParams = this.pathParams || {};
            const path = substituteTemplate(template, pathParams);
            const inTemplate = this.templateArgNames;
            const helpBy = this.helpByParameterName;
            const encodeVal = this.urlEncodeQueryParams !== false;
            const parts = [];
            for (const arg of this.pathArguments) {
                if (arg.Direction === 1) continue;
                if (arg.PostDataContent) continue;
                const name = arg.Name;
                if (!name) continue;
                if (inTemplate.has(name)) continue;
                const testVal = pathParams[name] != null ? String(pathParams[name]) : '';
                const defVal = argDefaultString(arg, helpBy);
                if (testValueEqualsDefault(testVal, defVal)) continue;
                const encName = encodeURIComponent(name);
                const v = encodeVal ? encodeURIComponent(testVal) : testVal;
                parts.push(`${encName}=${v}`);
            }
            if (!parts.length) return path;
            const sep = path.includes('?') ? '&' : '?';
            return path + sep + parts.join('&');
        },
        /** URL used for execute: optional user override of computed path. */
        effectiveRequestUrl() {
            if (this.urlOverrideEnabled) {
                const o = (this.urlOverride || '').trim();
                if (o.length) return o;
            }
            return this.builtUrl;
        },
        visibleArguments() {
            const args = this.metadata?.Arguments;
            if (!Array.isArray(args)) return [];
            // Excluding arguments that are inferred automatically by the web server ({$Current} should be hidden)
            // DO NOT Remove the filter on $Current.
            return args.filter((a) => !a.Hidden && !a.HiddenFromWeb && !isAutoContextArg(a) && (a.DefaultValue !== '{$Current}'));
        },
        pathArguments() {
            return this.visibleArguments.filter((a) => !a.PostDataContent);
        },
        hasPostBody() {
            if (this.visibleArguments.some((a) => a.PostDataContent)) return true;
            const pd = this.helpDoc?.PostData;
            return typeof pd === 'string' && pd.length > 0;
        },
        helpByParameterName() {
            const map = {};
            const params = this.helpDoc?.Parameters;
            if (Array.isArray(params)) {
                for (const p of params) {
                    if (p?.Name) map[p.Name] = p;
                }
            }
            return map;
        },
        parameterRows() {
            const rows = [];
            const can = this.canEditMethodDescriptions;
            for (const arg of this.visibleArguments) {
                const h = this.helpByParameterName[arg.Name];
                let required = false;
                if (h && typeof h.Required === 'boolean') required = h.Required;
                else {
                    const dv = arg.DefaultValue;
                    required =
                        (dv === undefined || dv === null || dv === '') && !arg.DefaultAllowsNull;
                }
                const helpDescRaw =
                    h?.Description && String(h.Description).trim() ? String(h.Description) : '';
                const useHelp = can && !!helpDescRaw;
                const metadataDescription = String(arg.Description ?? '');
                let description;
                let descriptionSource = 'metadata';
                let helpHtml = null;
                if (useHelp) {
                    descriptionSource = 'help';
                    helpHtml = helpDescRaw;
                    description = stripHtml(helpDescRaw);
                } else if (!can && h?.Description) {
                    description = stripHtml(h.Description);
                } else {
                    description = metadataDescription;
                }
                let defVal = '';
                const hDef = h?.DefaultValue;
                const aDef = arg.DefaultValue;
                if (hDef != null && String(hDef).trim() !== '') defVal = String(hDef);
                else if (aDef != null && String(aDef).trim() !== '') defVal = String(aDef);
                const dir = arg.Direction;
                const direction = dir === 1 ? 'output' : 'input';
                rows.push({
                    name: arg.Name,
                    type: h?.DataType || arg.ArgType || '',
                    description,
                    descriptionSource,
                    helpHtml,
                    metadataDescription,
                    required,
                    postBody: !!arg.PostDataContent,
                    defaultValue: defVal,
                    direction,
                });
            }
            return rows;
        },
        sampleOptions() {
            const samples = this.helpDoc?.Samples;
            if (!Array.isArray(samples) || !samples.length) return [];
            return samples.map((s, i) => ({
                label: s.Language || `Sample ${i + 1}`,
                value: i,
            }));
        },
        currentSample() {
            const samples = this.helpDoc?.Samples;
            if (!Array.isArray(samples) || !samples.length) return null;
            const i = Math.min(this.sampleLangIndex, samples.length - 1);
            return samples[i] || null;
        },
        /** Rich HTML from Apps/Help/ApiDocumentation only. */
        documentationHtml() {
            const hd = this.helpDoc?.Description;
            if (hd && typeof hd === 'string' && hd.length) return hd;
            return '';
        },
        hasHelpDocumentation() {
            return !!this.documentationHtml?.trim();
        },
        /** Help tab: rich Description and/or SampleResponses from ApiDocumentation. */
        hasHelpDocTabContent() {
            if (this.hasHelpDocumentation) return true;
            const sr = this.helpDoc?.SampleResponses;
            return Array.isArray(sr) && sr.length > 0;
        },
        hasHelpCodeSamples() {
            return this.sampleOptions.length > 0;
        },
        /** Plain summary: metadata Description when loaded, else tree selection hint. */
        summaryDescription() {
            if (
                this.metadata &&
                this.metadata.Description != null &&
                String(this.metadata.Description).trim() !== ''
            ) {
                return String(this.metadata.Description).trim();
            }
            const r = this.resolvedDescription;
            return typeof r === 'string' ? r.trim() : '';
        },
        hasResponseToInspect() {
            return !!(this.lastResultDisplay && String(this.lastResultDisplay).trim()) || !!this.lastError;
        },
        responseDialogContent() {
            const body = (this.lastResultDisplay && String(this.lastResultDisplay).trim())
                ? String(this.lastResultDisplay)
                : '';
            if (body) return body;
            if (this.lastError) return `Error:\n${this.lastError}`;
            return '';
        },
        responseDialogAceLang() {
            const t = (this.responseDialogContent || '').trim();
            if (t.startsWith('{') || t.startsWith('[')) return 'json';
            return 'text';
        },
        implementationJson() {
            const gi = this.metadata?.GeneralImplementation;
            if (!gi) return '';
            try {
                return JSON.stringify(gi, null, 2);
            } catch (e) {
                return String(gi);
            }
        },
        /** 'dynamic' | 'user' | 'other' | null */
        implementationKind() {
            const gi = this.metadata?.GeneralImplementation;
            if (!gi) return null;
            if (gi.$type === DYNAMIC_IMPL_TYPE && gi.CLRImpl) return 'dynamic';
            if (gi.$type === USER_IMPL_TYPE) return 'user';
            return 'other';
        },
        metadataUniqueKey() {
            const m = this.metadata;
            if (!m) return '';
            return String(m.Key ?? m._id ?? '');
        },
        securityContextTypeLabel() {
            return getSecurityContextType(this.metadata);
        },
        /** metadata.OwnerID → System / Current Call Center / 3rd Party */
        ownerImplementationLabel() {
            const m = this.metadata;
            if (!m) return '—';
            const oid = m.OwnerID;
            const idStr = oid != null && oid !== '' ? String(oid) : '';
            if (idStr === '50') return 'System';
            const svc = this.System?.careTokenService;
            let customerId = '';
            if (svc && typeof svc.CustomerID === 'function') {
                try {
                    customerId = String(svc.CustomerID());
                } catch (e) {
                    customerId = '';
                }
            }
            if (idStr && customerId && idStr === customerId) return 'Current Call Center';
            return '3rd Party';
        },
        /** Owner may edit method + parameter descriptions (metadata plain; help rich when help wins). */
        canEditMethodDescriptions() {
            const m = this.metadata;
            if (!m) return false;
            const svc = this.System?.careTokenService;
            let customerId = '';
            if (svc && typeof svc.CustomerID === 'function') {
                try {
                    customerId = String(svc.CustomerID());
                } catch (e) {
                    customerId = '';
                }
            }
            return customerId !== '' && customerId === String(m.OwnerID ?? '');
        },
        metadataDescriptionDirty() {
            if (!this.metadata || !this._metadataBaselineJson) return false;
            try {
                return this._metadataBaselineJson !== JSON.stringify(this.metadata);
            } catch (e) {
                return false;
            }
        },
        helpDocDirty() {
            if (!this.helpDoc || !this._helpDocBaselineJson) return false;
            try {
                return this._helpDocBaselineJson !== JSON.stringify(this.helpDoc);
            } catch (e) {
                return false;
            }
        },
        descriptionEditDirty() {
            return this.metadataDescriptionDirty || this.helpDocDirty;
        },
    },
    watch: {
        resolvedKey: {
            immediate: true,
            handler() {
                this.onSelectionChanged();
            },
        },
        hasHelpDocTabContent(val) {
            if (!val && this.mainTab === 'doc') this.mainTab = 'try';
        },
        hasHelpCodeSamples(val) {
            if (!val && this.mainTab === 'samples') this.mainTab = 'try';
        },
        responseDialogOpen(open) {
            if (open) {
                nextTick(() => this.responseAceEditor?.resize?.());
            }
        },
    },
    methods: {
        evalControlField(fieldName) {
            // Do Not Remove the API prefix - the schema adds this so we must account for it:
            fieldName = 'API' + fieldName;

            const cd = this.controlData;
            if (!cd || !(fieldName in cd)) {
                if (fieldName === 'Public') return false;
                return '';
            }
            const raw = cd[fieldName];
            if (typeof raw !== 'string') return raw;

            let cache = this.interpCache[fieldName];
            if (!cache || cache.source !== raw) {
                try {
                    const expr = utils.compile(this, raw);
                    cache = { source: raw, expr };
                } catch (e) {
                    utils.warn(`APITester: could not compile ${fieldName}`, e);
                    cache = { source: raw, expr: null };
                }
                this.interpCache[fieldName] = cache;
            }
            if (cache.expr) {
                try {
                    return utils.evaluate(cache.expr, this);
                } catch (e) {
                    utils.warn(`APITester: could not evaluate ${fieldName}`, e);
                    return '';
                }
            }
            return raw;
        },
        storageKey() {
            const k = this.resolvedKey;
            if (!k) return null;
            return STORAGE_PREFIX + k;
        },
        loadPersisted() {
            const sk = this.storageKey();
            if (!sk || typeof localStorage === 'undefined') return null;
            try {
                const raw = localStorage.getItem(sk);
                if (!raw) return null;
                return JSON.parse(raw);
            } catch (e) {
                return null;
            }
        },
        schedulePersist() {
            if (this.persistTimer) clearTimeout(this.persistTimer);
            this.persistTimer = setTimeout(() => this.persistNow(), 350);
        },
        persistNow() {
            const sk = this.storageKey();
            if (!sk || typeof localStorage === 'undefined') return;
            try {
                localStorage.setItem(
                    sk,
                    JSON.stringify({
                        pathParams: { ...this.pathParams },
                        postBody: this.postBody,
                        urlOverrideEnabled: this.urlOverrideEnabled,
                        urlOverride: this.urlOverride,
                        urlEncodeQueryParams: this.urlEncodeQueryParams,
                    })
                );
            } catch (e) {
                utils.warn('APITester: persist failed', e);
            }
        },
        applyDefaultsFromMetadata() {
            const next = {};
            for (const arg of this.pathArguments) {
                const dv = arg.DefaultValue;
                next[arg.Name] = dv !== undefined && dv !== null ? String(dv) : '';
            }
            this.pathParams = next;
        },
        mergePersisted(persisted) {
            if (!persisted || typeof persisted !== 'object') return;
            if (persisted.pathParams && typeof persisted.pathParams === 'object') {
                this.pathParams = { ...this.pathParams, ...persisted.pathParams };
            }
            if (typeof persisted.postBody === 'string') {
                this.postBody = persisted.postBody;
            }
            if (typeof persisted.urlOverrideEnabled === 'boolean') {
                this.urlOverrideEnabled = persisted.urlOverrideEnabled;
            }
            if (typeof persisted.urlOverride === 'string') {
                this.urlOverride = persisted.urlOverride;
            }
            if (typeof persisted.urlEncodeQueryParams === 'boolean') {
                this.urlEncodeQueryParams = persisted.urlEncodeQueryParams;
            }
        },
        initPostBodyFromHelp() {
            const pd = this.helpDoc?.PostData;
            if (typeof pd === 'string' && pd.length && !this.postBody.trim()) {
                this.postBody = pd;
            }
        },
        async onSelectionChanged() {
            this.fetchGeneration++;
            const gen = this.fetchGeneration;
            this.metadata = null;
            this.helpDoc = null;
            this.loadError = null;
            this.helpLoadError = null;
            this.lastResult = null;
            this.lastResultDisplay = '';
            this.lastError = null;
            this.sampleLangIndex = 0;
            this.urlOverrideEnabled = false;
            this.urlOverride = '';
            this.urlEncodeQueryParams = true;
            this.paramDescExpanded = {};
            this._metadataBaselineJson = '';
            this._helpDocBaselineJson = '';
            this.editingSummaryDescription = false;
            this.editingParamDescName = null;
            this.paramRichDialogOpen = false;
            this.paramRichDialogParamName = '';
            this.paramRichDialogInitialHtml = '';
            this.descriptionSaveError = null;

            const key = this.resolvedKey;
            if (!key) {
                this.loading = false;
                return;
            }

            this.loading = true;
            try {
                const methodUrl = `Apps/Method/${encodeURIComponent(key)}`;
                const owner = encodeURIComponent(this.resolvedOwnerID || '');
                const helpUrl = `Apps/Help/ApiDocumentation?ApiKey=${encodeURIComponent(key)}&ErrorOnMissing=false&OwnerID=${owner}`;

                const [meta, help] = await Promise.all([
                    utils.api.get(methodUrl).catch((e) => {
                        throw e;
                    }),
                    utils.api.get(helpUrl).catch((e) => {
                        this.helpLoadError = this.formatThrownError(e);
                        return null;
                    }),
                ]);

                if (gen !== this.fetchGeneration) return;

                this.metadata = meta;
                this.helpDoc = help && typeof help === 'object' ? help : null;

                this.applyDefaultsFromMetadata();
                const persisted = this.loadPersisted();
                this.mergePersisted(persisted);
                this.initPostBodyFromHelp();
                this.snapshotDescriptionBaselines();
                this.schedulePersist();
            } catch (e) {
                if (gen !== this.fetchGeneration) return;
                this.loadError = this.formatThrownError(e);
                utils.error('APITester: metadata load failed', e);
            } finally {
                if (gen === this.fetchGeneration) this.loading = false;
            }
        },
        setPathParam(name, value) {
            this.pathParams = { ...this.pathParams, [name]: value ?? '' };
            this.schedulePersist();
        },
        setPostBody(value) {
            this.postBody = value ?? '';
            this.schedulePersist();
        },
        setUrlOverrideEnabled(value) {
            const on = !!value;
            if (on && !(this.urlOverride || '').trim()) {
                this.urlOverride = this.builtUrl || '';
            }
            this.urlOverrideEnabled = on;
            this.schedulePersist();
        },
        setUrlOverride(value) {
            this.urlOverride = value ?? '';
            this.schedulePersist();
        },
        setUrlEncodeQueryParams(value) {
            this.urlEncodeQueryParams = !!value;
            this.schedulePersist();
        },
        formatResponsePayload(data) {
            if (data === null || data === undefined) return '';
            if (typeof data === 'string') {
                try {
                    const parsed = JSON.parse(data);
                    return JSON.stringify(parsed, null, 2);
                } catch (e) {
                    return data;
                }
            }
            if (typeof data === 'object') {
                try {
                    return JSON.stringify(data, null, 2);
                } catch (e) {
                    return String(data);
                }
            }
            return String(data);
        },
        /** Plain objects from apiService often use statusCode/reasonPhrase (no Error.message). */
        formatThrownError(e) {
            if (e == null || e === '') return 'Request failed.';
            if (typeof e === 'string') return e;
            if (e instanceof Error && e.message) return e.message;
            if (typeof e === 'object') {
                const code = e.statusCode ?? e.StatusCode;
                let reason = e.reasonPhrase ?? e.ReasonPhrase ?? e.message ?? e.Message;
                if (reason != null && typeof reason === 'object') {
                    try {
                        reason = JSON.stringify(reason);
                    } catch (_) {
                        reason = String(reason);
                    }
                }
                const body = e.responseBody ?? e.ResponseBody;
                let bodyStr = '';
                if (body != null && body !== '') {
                    bodyStr =
                        typeof body === 'string'
                            ? body
                            : (() => {
                                  try {
                                      return JSON.stringify(body, null, 2);
                                  } catch (_) {
                                      return String(body);
                                  }
                              })();
                }
                if (reason != null && String(reason).trim() !== '') {
                    const prefix = code != null && code !== '' ? `${code} ` : '';
                    const main = `${prefix}${String(reason).trim()}`.trim();
                    if (bodyStr) return `${main}\n\n${bodyStr}`;
                    return main;
                }
                if (bodyStr) return bodyStr;
                if (code != null && code !== '') return `Request failed (HTTP ${code}).`;
                try {
                    return JSON.stringify(e);
                } catch (_) {
                    return String(e);
                }
            }
            return String(e);
        },
        emitExecuteToast(successful, subtext) {
            if (successful) {
                EventBus.$emit('v-toast', {
                    text: 'Success',
                    subtext: subtext || 'The operation completed successfully',
                    type: 'success',
                    icon: 'mdi mdi-check',
                    context: this,
                });
                return;
            }
            EventBus.$emit('v-toast', {
                text: 'Error',
                subtext: subtext || 'Unknown error',
                type: 'error',
                icon: 'mdi mdi-alert',
                context: this,
            });
        },
        async executeApi() {
            const url = this.effectiveRequestUrl;
            if (!url) {
                this.lastError = this.urlOverrideEnabled
                    ? 'Enter a custom URL or disable override to use the computed URL.'
                    : 'No request URL (template + parameters).';
                this.emitExecuteToast(false, this.lastError);
                return;
            }
            const verb = this.httpVerb;
            this.executing = true;
            this.lastError = null;
            this.lastResult = null;
            this.lastResultDisplay = '';

            let bodyData = undefined;
            if (verb !== 'GET' && verb !== 'DELETE' && this.hasPostBody) {
                const raw = this.postBody.trim();
                if (raw) {
                    try {
                        bodyData = JSON.parse(raw);
                    } catch (e) {
                        this.lastError = 'POST body must be valid JSON: ' + this.formatThrownError(e);
                        this.executing = false;
                        this.emitExecuteToast(false, this.lastError);
                        return;
                    }
                }
            }

            try {
                let result;
                if (verb === 'GET') {
                    result = await utils.api.get(url);
                } else {
                    result = await utils.api.request(verb, url, bodyData, false, false);
                }
                this.lastResult = result;
                this.lastResultDisplay = this.formatResponsePayload(result);
                this.emitExecuteToast(true);
            } catch (e) {
                this.lastError = this.formatThrownError(e);
                utils.warn('APITester: execute failed', e);
                this.emitExecuteToast(false, this.lastError);
            } finally {
                this.executing = false;
            }
        },
        async preRenderComplete() {
            this.finishRenderHandler(this);
        },
        implDetailRow(label, value) {
            const text = value != null && value !== '' ? String(value) : '—';
            return (
                <div class="row q-col-gutter-sm q-mb-sm items-start">
                    <div class="col-12 col-sm-4 text-weight-medium text-grey-8">{label}</div>
                    <div class="col-12 col-sm-8 text-body2" style={{ wordBreak: 'break-word' }}>
                        {text}
                    </div>
                </div>
            );
        },
        toggleParamDescExpand(paramName) {
            const next = { ...this.paramDescExpanded };
            next[paramName] = !next[paramName];
            this.paramDescExpanded = next;
        },
        snapshotDescriptionBaselines() {
            try {
                this._metadataBaselineJson = this.metadata ? JSON.stringify(this.metadata) : '';
            } catch (e) {
                this._metadataBaselineJson = '';
            }
            try {
                this._helpDocBaselineJson = this.helpDoc ? JSON.stringify(this.helpDoc) : '';
            } catch (e) {
                this._helpDocBaselineJson = '';
            }
        },
        cancelDescriptionEdits() {
            if (this._metadataBaselineJson) {
                try {
                    this.metadata = JSON.parse(this._metadataBaselineJson);
                } catch (e) {
                    utils.warn('APITester: cancel metadata parse failed', e);
                }
            }
            if (this._helpDocBaselineJson) {
                try {
                    this.helpDoc = JSON.parse(this._helpDocBaselineJson);
                } catch (e) {
                    utils.warn('APITester: cancel helpDoc parse failed', e);
                }
            }
            this.editingSummaryDescription = false;
            this.editingParamDescName = null;
            this.paramRichDialogOpen = false;
            this.descriptionSaveError = null;
        },
        applyRevisionFromMethodv3Response(result) {
            if (!this.metadata) return;
            let r = result;
            if (r != null && typeof r === 'object' && 'Revision' in r) {
                this.metadata = { ...this.metadata, _rev: r.Revision };
                return;
            }
            if (
                r != null &&
                typeof r === 'object' &&
                r.Result != null &&
                typeof r.Result === 'object' &&
                'Revision' in r.Result
            ) {
                this.metadata = { ...this.metadata, _rev: r.Result.Revision };
            }
        },
        async refetchHelpDocOnly() {
            const key = this.resolvedKey;
            if (!key) return;
            const owner = encodeURIComponent(this.resolvedOwnerID || '');
            const helpUrl = `Apps/Help/ApiDocumentation?ApiKey=${encodeURIComponent(key)}&ErrorOnMissing=false&OwnerID=${owner}`;
            const help = await utils.api.get(helpUrl).catch((e) => {
                this.helpLoadError = this.formatThrownError(e);
                throw e;
            });
            this.helpDoc = help && typeof help === 'object' ? help : null;
            this.snapshotDescriptionBaselines();
        },
        async saveDescriptionEdits() {
            this.descriptionSaveError = null;
            this.savingDescriptionEdits = true;
            let metaPostOk = true;
            try {
                if (this.metadataDescriptionDirty) {
                    try {
                        const res = await utils.api.request(
                            'POST',
                            'Apps/Methodv3',
                            this.metadata,
                            false,
                            false
                        );
                        this.applyRevisionFromMethodv3Response(res);
                        this._metadataBaselineJson = JSON.stringify(this.metadata);
                    } catch (e) {
                        metaPostOk = false;
                        this.descriptionSaveError = this.formatThrownError(e);
                        utils.warn('APITester: Methodv3 save failed', e);
                    }
                }
                if (metaPostOk && this.helpDocDirty && this.helpDoc) {
                    try {
                        await utils.api.request(
                            'POST',
                            'Apps/Help/ApiDocumentation',
                            this.helpDoc,
                            false,
                            false
                        );
                        await this.refetchHelpDocOnly();
                    } catch (e) {
                        this.descriptionSaveError = this.formatThrownError(e);
                        utils.warn('APITester: ApiDocumentation save/refetch failed', e);
                    }
                }
                if (metaPostOk) {
                    this.editingSummaryDescription = false;
                    this.editingParamDescName = null;
                }
            } finally {
                this.savingDescriptionEdits = false;
            }
        },
        setMetadataRootDescription(value) {
            if (!this.metadata) return;
            this.metadata = { ...this.metadata, Description: value ?? '' };
        },
        getMetadataArgDescription(name) {
            const a = this.metadata?.Arguments?.find((x) => x.Name === name);
            return a ? String(a.Description ?? '') : '';
        },
        setMetadataArgDescription(name, value) {
            if (!this.metadata?.Arguments) return;
            const i = this.metadata.Arguments.findIndex((x) => x.Name === name);
            if (i < 0) return;
            const nextArgs = [...this.metadata.Arguments];
            nextArgs[i] = { ...nextArgs[i], Description: value ?? '' };
            this.metadata = { ...this.metadata, Arguments: nextArgs };
        },
        onParamDescPencil(row) {
            if (!this.canEditMethodDescriptions || !row) return;
            if (row.descriptionSource === 'help') {
                this.paramRichDialogParamName = row.name;
                const p = this.helpDoc?.Parameters?.find((x) => x.Name === row.name);
                this.paramRichDialogInitialHtml =
                    p?.Description != null ? String(p.Description) : (row.helpHtml || '');
                this.paramRichDialogOpen = true;
            } else {
                this.editingParamDescName =
                    this.editingParamDescName === row.name ? null : row.name;
            }
        },
        onParamRichDialogSave(html) {
            const name = this.paramRichDialogParamName;
            const params = this.helpDoc?.Parameters;
            if (!name || !Array.isArray(params)) return;
            const i = params.findIndex((p) => p.Name === name);
            if (i < 0) {
                utils.warn(`APITester: help parameter not found: ${name}`);
                return;
            }
            const nextParams = [...params];
            nextParams[i] = { ...nextParams[i], Description: html };
            this.helpDoc = { ...this.helpDoc, Parameters: nextParams };
        },
        onResponseAceInit(editor) {
            this.responseAceEditor = editor;
            nextTick(() => editor?.resize?.());
        },
        scheduleResponseAceResize() {
            nextTick(() => this.responseAceEditor?.resize?.());
        },
    },
    render() {
        if (!this.todisplay) return null;

        try {
            const verb = this.httpVerb;
            const warnBanner = this.showLiveWarning ? (
                <QBanner class="bg-amber-2 text-dark q-mb-md" rounded>
                    <div class="text-body2">
                        This panel calls your live API using your current session. Use care on production systems.
                    </div>
                </QBanner>
            ) : null;

            const loadErr = this.loadError ? (
                <QBanner class="bg-red-2 text-dark q-mb-md" rounded>
                    {this.loadError}
                </QBanner>
            ) : null;

            const header = (
                <QCard flat bordered class="q-mb-md full-width">
                    <QCardSection class="q-pa-md">
                        <div class="row items-center q-gutter-sm q-mb-sm">
                            <QChip dense color={verbColor(verb)} text-color="white">
                                {verb}
                            </QChip>
                            <div class="text-h6">{this.displayName}</div>
                            {this.resolvedNamespace ? (
                                <QChip dense outline color="grey-7">
                                    {this.resolvedNamespace}
                                </QChip>
                            ) : null}
                            {this.resolvedPublic ? (
                                <QChip dense outline color="positive">
                                    Public
                                </QChip>
                            ) : null}
                        </div>
                        <div class="text-body2 text-grey-8 text-weight-medium q-mb-xs">Route template</div>
                        <QInput
                            dense
                            outlined
                            readonly
                            modelValue={this.templatePath || '—'}
                            class="c-APITester-url full-width"
                        />
                    </QCardSection>
                </QCard>
            );

            const showSummaryCard =
                (this.summaryDescription.length > 0 ||
                    (this.canEditMethodDescriptions && this.metadata)) &&
                this.metadata;

            const summaryCard = showSummaryCard ? (
                <QCard
                    flat
                    bordered
                    class="q-mb-md full-width"
                    style={{ borderLeft: '4px solid var(--q-primary)' }}
                >
                    <QCardSection class="q-py-sm q-px-md">
                        <div class="row items-center no-wrap q-mb-xs" style={{ gap: '8px' }}>
                            <div class="text-caption text-primary text-uppercase text-weight-bold">
                                About this API
                            </div>
                            {this.canEditMethodDescriptions ? (
                                <QBtn
                                    round
                                    flat
                                    dense
                                    size="sm"
                                    color="primary"
                                    icon="mdi-pencil"
                                    disable={this.savingDescriptionEdits}
                                    onClick={() => {
                                        this.editingSummaryDescription = !this.editingSummaryDescription;
                                    }}
                                >
                                    <QTooltip anchor="top middle" self="bottom middle">
                                        Edit API description (saved with method metadata)
                                    </QTooltip>
                                </QBtn>
                            ) : null}
                        </div>
                        {this.editingSummaryDescription && this.canEditMethodDescriptions ? (
                            <QInput
                                type="textarea"
                                autogrow
                                dense
                                outlined
                                class="q-mt-xs full-width"
                                modelValue={String(this.metadata.Description ?? '')}
                                onUpdate:modelValue={(v) => this.setMetadataRootDescription(v)}
                            />
                        ) : (
                            <div class="text-body1 text-weight-medium" style={{ lineHeight: 1.45 }}>
                                {this.summaryDescription}
                            </div>
                        )}
                    </QCardSection>
                </QCard>
            ) : null;

            const descriptionSaveBar =
                this.canEditMethodDescriptions && this.descriptionEditDirty ? (
                    <QCard flat bordered class="q-mb-md full-width bg-blue-grey-1">
                        <QCardSection class="row items-center q-gutter-sm q-py-sm flex-wrap">
                            <span class="text-body2 text-weight-medium">Unsaved description changes</span>
                            <QSpace />
                            <QBtn
                                flat
                                dense
                                icon="mdi-close"
                                label="Cancel"
                                disable={this.savingDescriptionEdits}
                                onClick={() => this.cancelDescriptionEdits()}
                            />
                            <QBtn
                                unelevated
                                dense
                                color="primary"
                                icon="mdi-content-save"
                                label="Save"
                                loading={this.savingDescriptionEdits}
                                disable={this.savingDescriptionEdits}
                                onClick={() => this.saveDescriptionEdits()}
                            />
                            {this.descriptionSaveError ? (
                                <span class="text-negative text-body2 col-12 q-mt-xs">
                                    {this.descriptionSaveError}
                                </span>
                            ) : null}
                        </QCardSection>
                    </QCard>
                ) : null;

            const gi = this.metadata?.GeneralImplementation;
            const kind = this.implementationKind;
            const clr = gi?.CLRImpl;
            const clrTypeLine =
                clr &&
                [clr.FullyQualifiedTypeName, clr.AssemblyName].filter((x) => x != null && String(x) !== '').join(', ');

            const implHeaderTooltip =
                'Shows how this API is implemented (CLR assembly method or Flow project), the document unique key, owner scope (System / your call center / third party), and security context. Raw JSON is available inside when needed.';

            const implInnerBody = (
                <QCard flat bordered class="q-ma-none">
                    <QCardSection class="q-pt-sm q-pb-md">
                        {kind === 'dynamic' && clr ? (
                            <div>
                                {this.implDetailRow('CLR Method Name', clr.MethodName)}
                                {this.implDetailRow('CLR Type', clrTypeLine || '—')}
                            </div>
                        ) : null}
                        {kind === 'user' ? (
                            <div>
                                {this.implDetailRow('Flow Project', gi.ProjectName)}
                                {this.implDetailRow(
                                    'Namespace',
                                    [gi.ClassName, gi.MethodName].every((x) => x == null || String(x) === '')
                                        ? '—'
                                        : `${gi.ClassName ?? ''}.${gi.MethodName ?? ''}`
                                )}
                            </div>
                        ) : null}
                        {kind === 'other' ? (
                            <div class="text-body2 text-grey-7 q-mb-md">
                                Specialized implementation details are not available for this type; see raw JSON
                                below.
                            </div>
                        ) : null}
                        {this.implDetailRow('Unique Key', this.metadataUniqueKey)}
                        {this.implDetailRow('Owner', this.ownerImplementationLabel)}
                        {this.implDetailRow('Security Context', this.securityContextTypeLabel)}
                        {this.implementationJson ? (
                            <div>
                                <QSeparator class="q-my-md" />
                                <QExpansionItem
                                    dense
                                    header-class="text-caption text-grey-8 q-py-xs"
                                    label="Raw implementation (JSON)"
                                    default-opened={false}
                                >
                                    <QInput
                                        type="textarea"
                                        readonly
                                        filled
                                        class="q-mt-sm"
                                        modelValue={this.implementationJson}
                                        input-style="font-family: monospace; font-size: 12px;"
                                        rows={10}
                                    />
                                </QExpansionItem>
                            </div>
                        ) : null}
                    </QCardSection>
                </QCard>
            );

            const implBlock = gi ? (
                <QExpansionItem
                    dense
                    hideExpandIcon
                    class="q-mb-md full-width api-tester-impl-expansion"
                    header-class="q-pa-none"
                    default-opened={false}
                    v-slots={{
                        header: (props) => (
                            <div
                                class="row no-wrap items-center full-width q-py-xs q-px-sm"
                                style={{ minHeight: '30px' }}
                                onClick={() => props.toggle()}
                            >
                                <QBtn
                                    round
                                    flat
                                    dense
                                    size="sm"
                                    icon={props.expanded ? 'expand_less' : 'expand_more'}
                                    color="grey-7"
                                    tabindex={-1}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        props.toggle();
                                    }}
                                />
                                <span class="text-subtitle2 text-weight-medium q-ml-xs">Implementation</span>
                                <div class="col-auto q-ml-xs" onClick={(e) => e.stopPropagation()}>
                                    <QIcon name="mdi-information-outline" size="18px" color="grey-6" class="cursor-help">
                                        <QTooltip
                                            anchor="top middle"
                                            self="bottom middle"
                                            class="bg-grey-9 text-body2"
                                            style={{ maxWidth: '320px' }}
                                        >
                                            {implHeaderTooltip}
                                        </QTooltip>
                                    </QIcon>
                                </div>
                            </div>
                        ),
                        default: () => implInnerBody,
                    }}
                />
            ) : null;

            const paramRows = this.parameterRows.map((row, idx) => {
                const descExpanded = !!this.paramDescExpanded[row.name];
                const desc = row.description || '';
                const descLong = desc.length > PARAM_DESC_PREVIEW;
                const descDisplay =
                    descExpanded || !descLong ? desc : `${desc.slice(0, PARAM_DESC_PREVIEW).trimEnd()}…`;
                const stripeClass = idx % 2 === 0 ? 'bg-grey-2' : '';
                const helpPreview =
                    row.descriptionSource === 'help' && this.canEditMethodDescriptions && row.helpHtml ? (
                        <div
                            class="form-html-editor-prose text-body2 api-tester-param-help-preview"
                            style={{
                                maxHeight: descExpanded ? 'none' : '72px',
                                overflow: descExpanded ? 'visible' : 'hidden',
                                lineHeight: '1.35',
                                wordBreak: 'break-word',
                            }}
                            innerHTML={row.helpHtml}
                        ></div>
                    ) : (
                        <div
                            class="text-body2"
                            style={
                                descExpanded
                                    ? { wordBreak: 'break-word', lineHeight: '1.35' }
                                    : {
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          wordBreak: 'break-word',
                                          lineHeight: '1.35',
                                      }
                            }
                        >
                            {descDisplay || '—'}
                        </div>
                    );
                const metaInlineEdit =
                    this.canEditMethodDescriptions &&
                    row.descriptionSource === 'metadata' &&
                    this.editingParamDescName === row.name;
                const descCellBody = metaInlineEdit ? (
                    <QInput
                        type="textarea"
                        autogrow
                        dense
                        outlined
                        class="full-width"
                        modelValue={this.getMetadataArgDescription(row.name)}
                        onUpdate:modelValue={(v) => this.setMetadataArgDescription(row.name, v)}
                    />
                ) : (
                    helpPreview
                );
                return (
                    <tr key={row.name} class={['api-tester-param-tr', stripeClass]}>
                        <td class="text-center q-px-xs" style={{ width: '36px', verticalAlign: 'middle' }}>
                            {row.direction === 'output' ? (
                                <QIcon name="mdi-export-variant" size="sm" color="deep-orange-8" class="cursor-help">
                                    <QTooltip
                                        anchor="top middle"
                                        self="bottom middle"
                                        class="bg-grey-9 text-body2"
                                        style={{ maxWidth: '280px' }}
                                    >
                                        Output — returned in the response body under{' '}
                                        <span class="text-weight-bold">Output</span>: {'{ ... }'}
                                    </QTooltip>
                                </QIcon>
                            ) : (
                                <QIcon name="mdi-import" size="sm" color="primary" class="cursor-help">
                                    <QTooltip
                                        anchor="top middle"
                                        self="bottom middle"
                                        class="bg-grey-9 text-body2"
                                        style={{ maxWidth: '260px' }}
                                    >
                                        Input — sent with the request
                                    </QTooltip>
                                </QIcon>
                            )}
                        </td>
                        <td class="text-left text-weight-medium q-px-xs" style={{ verticalAlign: 'top' }}>
                            {row.name}
                        </td>
                        <td
                            class="text-left text-grey-8 q-px-xs text-caption"
                            style={{ verticalAlign: 'top', maxWidth: '100px', wordBreak: 'break-word' }}
                        >
                            {row.type || '—'}
                        </td>
                        <td
                            class="text-left text-grey-7 q-px-xs text-caption"
                            style={{
                                verticalAlign: 'top',
                                maxWidth: '140px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            title={row.defaultValue || undefined}
                        >
                            {row.defaultValue ? row.defaultValue : '—'}
                        </td>
                        <td class="text-left q-px-xs" style={{ verticalAlign: 'top', maxWidth: 'min(38vw, 340px)' }}>
                            <div class="row no-wrap items-start q-gutter-xs">
                                <div class="col" style={{ minWidth: 0 }}>
                                    {descCellBody}
                                    {descLong && !(metaInlineEdit && row.descriptionSource === 'metadata') ? (
                                        <QBtn
                                            flat
                                            dense
                                            noCaps
                                            size="sm"
                                            color="primary"
                                            class="q-mt-xs q-pa-none"
                                            style={{ minHeight: '22px' }}
                                            label={descExpanded ? 'Show less' : 'Show more'}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                this.toggleParamDescExpand(row.name);
                                            }}
                                        />
                                    ) : null}
                                </div>
                                {this.canEditMethodDescriptions ? (
                                    <QBtn
                                        round
                                        flat
                                        dense
                                        size="sm"
                                        color="primary"
                                        icon="mdi-pencil"
                                        class="q-mt-none"
                                        style={{ flexShrink: 0 }}
                                        disable={this.savingDescriptionEdits}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.onParamDescPencil(row);
                                        }}
                                    >
                                        <QTooltip anchor="top middle" self="bottom middle">
                                            {row.descriptionSource === 'help'
                                                ? 'Edit rich description (API documentation)'
                                                : 'Edit description (method metadata)'}
                                        </QTooltip>
                                    </QBtn>
                                ) : null}
                            </div>
                        </td>
                        <td class="text-center q-px-xs text-caption" style={{ verticalAlign: 'top', width: '52px' }}>
                            {row.required ? (
                                <QIcon name="mdi-check-bold" size="xs" color="positive" />
                            ) : (
                                <span class="text-grey-5">—</span>
                            )}
                        </td>
                        <td class="text-center q-px-xs" style={{ verticalAlign: 'top', width: '44px' }}>
                            {row.postBody ? (
                                <QIcon name="mdi-database-arrow-down-outline" size="xs" color="grey-7" class="cursor-help">
                                    <QTooltip>Sent as POST body</QTooltip>
                                </QIcon>
                            ) : (
                                <span class="text-grey-5">—</span>
                            )}
                        </td>
                    </tr>
                );
            });

            const paramsTable =
                paramRows.length > 0 ? (
                    <QMarkupTable
                        dense
                        flat
                        bordered
                        wrap-cells
                        class="q-mb-md full-width api-tester-params-table"
                        style={{ fontSize: '12px', borderRadius: '8px', overflow: 'hidden' }}
                    >
                        <thead class="bg-blue-grey-7 text-white">
                            <tr>
                                <th class="text-center q-py-xs" style={{ width: '36px' }} title="Direction">
                                    I/O
                                </th>
                                <th class="text-left q-py-xs">Name</th>
                                <th class="text-left q-py-xs">Type</th>
                                <th class="text-left q-py-xs">Default</th>
                                <th class="text-left q-py-xs">Description</th>
                                <th class="text-center q-py-xs">Req</th>
                                <th class="text-center q-py-xs" title="POST body">
                                    Body
                                </th>
                            </tr>
                        </thead>
                        <tbody>{paramRows}</tbody>
                    </QMarkupTable>
                ) : null;

            const responseCard =
                this.metadata && (this.metadata.ReturnType || this.metadata.ReturnDescription) ? (
                    <QCard flat bordered class="q-mb-md full-width">
                        <QCardSection>
                            <div class="text-subtitle2 q-mb-sm">Response</div>
                            {this.metadata.ReturnType ? (
                                <div class="text-body2">
                                    <span class="text-weight-medium">Type:</span> {this.metadata.ReturnType}
                                </div>
                            ) : null}
                            {this.metadata.ReturnDescription ? (
                                <div class="text-body2 q-mt-xs">
                                    <span class="text-weight-medium">Description:</span>{' '}
                                    {this.metadata.ReturnDescription}
                                </div>
                            ) : null}
                            {this.configuration?.ContentType ? (
                                <div class="text-caption text-grey-7 q-mt-xs">
                                    Content-Type: {this.configuration.ContentType}
                                </div>
                            ) : null}
                        </QCardSection>
                    </QCard>
                ) : null;

            const computedUrlSection = (
                <div class="q-mb-lg full-width">
                    <div class="text-subtitle2 q-mb-xs">Computed request URL</div>
                    <QInput
                        dense
                        outlined
                        readonly={!this.urlOverrideEnabled}
                        class="full-width"
                        modelValue={
                            this.urlOverrideEnabled ? this.urlOverride || '' : this.builtUrl || ''
                        }
                        onUpdate:modelValue={(v) => {
                            if (this.urlOverrideEnabled) this.setUrlOverride(v);
                        }}
                        hint={
                            this.urlOverrideEnabled
                                ? 'Edit this path, or uncheck "Use custom URL" to use the value computed from the template and parameters.'
                                : 'Path comes from the route template; parameters not in the template are added as query strings when their value differs from the default. Template slots always use the current field values.'
                        }
                    />
                    <div class="row items-start q-mt-sm q-gutter-md wrap">
                        <div class="col-auto">
                            <QCheckbox
                                dense
                                label="Use custom URL for this request"
                                modelValue={this.urlOverrideEnabled}
                                onUpdate:modelValue={(v) => this.setUrlOverrideEnabled(v)}
                            />
                        </div>
                        <div class="col-auto">
                            <QCheckbox
                                dense
                                label="URL-encode query parameter values"
                                modelValue={this.urlEncodeQueryParams}
                                onUpdate:modelValue={(v) => this.setUrlEncodeQueryParams(v)}
                            />
                        </div>
                    </div>
                    <div class="text-caption text-grey-7 q-mt-sm">
                        <span class="text-weight-medium">Will call:</span>{' '}
                        {this.effectiveRequestUrl || '—'}
                    </div>
                </div>
            );

            const tryInputs = this.pathArguments.map((arg) => (
                <QInput
                    key={arg.Name}
                    class="q-mb-sm full-width"
                    dense
                    outlined
                    label={arg.Name}
                    hint={arg.Description || undefined}
                    modelValue={this.pathParams[arg.Name] ?? ''}
                    onUpdate:modelValue={(v) => this.setPathParam(arg.Name, v)}
                />
            ));

            const bodyInput =
                this.hasPostBody && this.httpVerb !== 'GET' && this.httpVerb !== 'DELETE' ? (
                    <div class="q-mt-md">
                        <div class="text-subtitle2 q-mb-xs">Request body (JSON)</div>
                        <QInput
                            type="textarea"
                            outlined
                            class="full-width"
                            modelValue={this.postBody}
                            onUpdate:modelValue={(v) => this.setPostBody(v)}
                            input-style="font-family: monospace; font-size: 13px;"
                            rows={10}
                        />
                    </div>
                ) : null;

            const execSection = (
                <div>
                    <div class="row items-center q-gutter-sm q-mb-md">
                        <QBtn
                            color="primary"
                            unelevated
                            no-caps
                            class="c-SentenceCaseButton"
                            loading={this.executing}
                            disable={this.executing || !this.effectiveRequestUrl}
                            onClick={() => this.executeApi()}
                        >
                            <span>{this.executing ? 'Executing…' : 'Execute'}</span>
                        </QBtn>
                        {this.lastError ? (
                            <span class="text-negative text-body2">{this.lastError}</span>
                        ) : null}
                    </div>
                    <QSeparator class="q-mb-md" />
                    <div class="row items-center justify-between q-mb-xs no-wrap" style={{ gap: '8px' }}>
                        <div class="text-subtitle2">Response</div>
                        <QBtn
                            dense
                            flat
                            color="primary"
                            icon="mdi-text-box-search-outline"
                            label="View in editor"
                            disable={!this.hasResponseToInspect}
                            onClick={() => {
                                this.responseDialogOpen = true;
                                nextTick(() => this.scheduleResponseAceResize());
                            }}
                        >
                            <QTooltip delay={400}>Open the last response (or error) in a resizable Ace editor</QTooltip>
                        </QBtn>
                    </div>
                    <QInput
                        type="textarea"
                        outlined
                        readonly
                        class="full-width"
                        modelValue={this.lastResultDisplay}
                        input-style="font-family: monospace; font-size: 12px;"
                        rows={14}
                    />
                </div>
            );

            const responseDialogShellStyle = {
                width: 'min(92vw, 1200px)',
                height: 'min(85vh, 900px)',
                maxWidth: '95vw',
                maxHeight: '92vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxSizing: 'border-box',
            };

            const responseDialog = (
                <QDialog
                    modelValue={this.responseDialogOpen}
                    onUpdate:modelValue={(v) => {
                        this.responseDialogOpen = v;
                        if (!v) this.responseAceEditor = null;
                    }}
                >
                    <QCard flat bordered style={responseDialogShellStyle} class="api-tester-response-dialog">
                        <QToolbar class="bg-primary text-white" style={{ flexShrink: 0 }}>
                            <QToolbarTitle class="text-white">API response</QToolbarTitle>
                            <QSpace />
                            <QBtn
                                flat
                                round
                                dense
                                color="white"
                                icon="mdi-close"
                                onClick={() => {
                                    this.responseDialogOpen = false;
                                    this.responseAceEditor = null;
                                }}
                            />
                        </QToolbar>
                        <QCardSection
                            class="q-pt-sm q-pb-md col"
                            style={{
                                flex: '1 1 auto',
                                minHeight: 0,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            <div
                                class="api-tester-response-ace-host"
                                style={{
                                    flex: '1 1 auto',
                                    minHeight: 0,
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <AceEditor
                                    lang={this.responseDialogAceLang}
                                    theme="github"
                                    readOnly={true}
                                    fontSize={13}
                                    content={this.responseDialogContent}
                                    onInit={(ed) => this.onResponseAceInit(ed)}
                                />
                            </div>
                        </QCardSection>
                    </QCard>
                </QDialog>
            );

            const helpErrNote = this.helpLoadError ? (
                <QBanner dense class="bg-grey-3 text-dark q-mb-sm" rounded>
                    Extended documentation could not be loaded ({this.helpLoadError}).
                </QBanner>
            ) : null;

            const hasSampleResponses =
                Array.isArray(this.helpDoc?.SampleResponses) && this.helpDoc.SampleResponses.length > 0;

            const docPanel = (
                <div>
                    {helpErrNote}
                    {this.hasHelpDocumentation ? (
                        <div class="q-pa-md" v-html={this.documentationHtml}></div>
                    ) : hasSampleResponses ? (
                        <QCardSection class="text-caption text-grey-7 q-pb-none">
                            No rich HTML description in Apps/Help/ApiDocumentation; sample responses are listed below.
                        </QCardSection>
                    ) : (
                        <QCardSection class="text-body2 text-grey-7">
                            No extended documentation is available from Apps/Help/ApiDocumentation for this API.
                        </QCardSection>
                    )}
                </div>
            );

            const samplesPanel =
                this.sampleOptions.length > 0 ? (
                    <div class="q-pa-md">
                        <QSelect
                            class="q-mb-md"
                            dense
                            outlined
                            label="Language"
                            options={this.sampleOptions}
                            modelValue={this.sampleLangIndex}
                            onUpdate:modelValue={(v) => {
                                this.sampleLangIndex = v;
                            }}
                            emit-value
                            map-options
                        />
                        {this.currentSample?.UsageRich ? (
                            <div
                                class="api-tester-sample-rich"
                                v-html={this.currentSample.UsageRich}
                            ></div>
                        ) : (
                            <div class="text-grey-7">No sample content.</div>
                        )}
                    </div>
                ) : (
                    <QCardSection class="text-grey-7">
                        No code samples are available for this API.
                    </QCardSection>
                );

            const sampleResponses =
                Array.isArray(this.helpDoc?.SampleResponses) && this.helpDoc.SampleResponses.length ? (
                    <QCard flat bordered class="q-mt-md">
                        <QCardSection>
                            <div class="text-subtitle2 q-mb-sm">Sample responses</div>
                            {this.helpDoc.SampleResponses.map((sr, idx) => (
                                <div key={idx} class="q-mb-md">
                                    <div class="text-caption">
                                        HTTP {sr.Code} {sr.ContentType ? `· ${sr.ContentType}` : ''}
                                    </div>
                                    <QInput
                                        type="textarea"
                                        readonly
                                        filled
                                        modelValue={sr.Value || ''}
                                        input-style="font-family: monospace; font-size: 12px;"
                                        rows={6}
                                    />
                                </div>
                            ))}
                        </QCardSection>
                    </QCard>
                ) : null;

            const tabsPanel = (
                <QCard flat bordered class="full-width">
                    <QTabs
                        modelValue={this.mainTab}
                        onUpdate:modelValue={(v) => {
                            if (v === 'doc' && !this.hasHelpDocTabContent) return;
                            if (v === 'samples' && !this.hasHelpCodeSamples) return;
                            this.mainTab = v;
                        }}
                        dense
                        class="text-grey-8"
                        active-color="primary"
                        indicator-color="primary"
                        align="left"
                    >
                        <QTab name="try" label="Try it" />
                        <QTab
                            name="doc"
                            label="Documentation"
                            disable={!this.hasHelpDocTabContent}
                            class={!this.hasHelpDocTabContent ? 'text-grey-5' : ''}
                        />
                        <QTab
                            name="samples"
                            label="Code samples"
                            disable={!this.hasHelpCodeSamples}
                            class={!this.hasHelpCodeSamples ? 'text-grey-5' : ''}
                        />
                    </QTabs>
                    <QSeparator />
                    <QTabPanels
                        class="full-width"
                        style={{ flex: '1 1 auto', minWidth: 0 }}
                        modelValue={this.mainTab}
                        onUpdate:modelValue={(v) => {
                            if (v === 'doc' && !this.hasHelpDocTabContent) return;
                            if (v === 'samples' && !this.hasHelpCodeSamples) return;
                            this.mainTab = v;
                        }}
                        animated
                    >
                        <QTabPanel name="try" class="q-pa-md">
                            <div class="text-body2 text-grey-8 q-mb-md">
                                Adjust parameters, review the computed URL, optionally override it, then execute.
                            </div>
                            {computedUrlSection}
                            {tryInputs}
                            {bodyInput}
                            {execSection}
                        </QTabPanel>
                        <QTabPanel name="doc">
                            {docPanel}
                            {sampleResponses}
                        </QTabPanel>
                        <QTabPanel name="samples">{samplesPanel}</QTabPanel>
                    </QTabPanels>
                </QCard>
            );

            const emptyState = !this.resolvedKey ? (
                <QCard flat bordered class="full-width">
                    <QCardSection class="text-grey-7">Select an API from the tree to view details.</QCardSection>
                </QCard>
            ) : null;

            return (
                <div
                    v-show={this.isvisible}
                    class={{ 'c-APITester': true, [`c-name-${this.name || 'unnamed'}`]: true }}
                    style={this.rootStyle}
                >
                    <QInnerLoading showing={this.loading} />
                    {warnBanner}
                    {loadErr}
                    {emptyState}
                    {this.resolvedKey && !this.loadError ? (
                        <div
                            class="column full-width"
                            style={{ flex: '1 1 auto', minWidth: 0, alignSelf: 'stretch' }}
                        >
                            {header}
                            {summaryCard}
                            {descriptionSaveBar}
                            <ApiTesterParamDescriptionDialog
                                modelValue={this.paramRichDialogOpen}
                                onUpdate:modelValue={(v) => {
                                    this.paramRichDialogOpen = v;
                                }}
                                paramName={this.paramRichDialogParamName}
                                initialHtml={this.paramRichDialogInitialHtml}
                                onSave={(html) => this.onParamRichDialogSave(html)}
                            />
                            {implBlock}
                            {paramsTable}
                            {responseCard}
                            {tabsPanel}
                            {responseDialog}
                        </div>
                    ) : null}
                </div>
            );
        } catch (e) {
            utils.error('APITester render failed', e);
            return <div class="text-negative">APITester failed to render: {String(e)}</div>;
        }
    },
});
